import { motion } from "framer-motion";
import { useRef } from "react";
import { useScroll, useTransform } from "framer-motion";
import { ArrowRight, Play, Radio } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IMAGES, VIDEOS } from "@/lib/data";
import { useState } from "react";
import VideoModal from "@/components/VideoModal";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.85, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] } }),
};

const HEADLINE = ["Building the next", "generation of America's", "maritime workforce"];

export default function Hero() {
  const ref = useRef(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.9]);

  return (
    <section id="top" ref={ref} className="relative min-h-[100svh] w-full overflow-hidden flex items-end">
      {/* Parallax backdrop with looping shipyard video over poster image */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={IMAGES.heroWelder} alt="Shipyard welder working on a hull in drydock" className="h-full w-full object-cover" />
        <video
          src={VIDEOS.heroLoop}
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 h-full w-full object-cover opacity-60 mix-blend-lighten"
        />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 z-10 bg-gradient-to-t from-[#0B0F14] via-[#0B0F14]/60 to-[#0B0F14]/30" />
      <div className="absolute inset-0 z-10 blueprint-grid opacity-40" />
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-[#0B0F14]/80 to-transparent" />

      {/* Coordinate badges */}
      <div className="absolute top-24 right-6 z-20 hidden md:block text-right">
        <div className="font-mono-tech text-[10px] tracking-[0.25em] text-[#8FA3B8]">[ SYS—PATH—01 ]</div>
        <div className="font-mono-tech text-[10px] tracking-[0.25em] text-[#FF5E1E] mt-1 flex items-center gap-1.5 justify-end">
          <Radio size={11} className="animate-pulse" /> LIVE COHORT RECRUITMENT
        </div>
      </div>

      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 pt-32 w-full">
        <motion.div
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="inline-flex items-center gap-2 border border-white/15 bg-white/5 backdrop-blur px-3 py-1.5 mb-6"
        >
          <span className="h-1.5 w-1.5 bg-[#10B981] rounded-full animate-pulse" />
          <span className="font-mono-tech text-[10px] tracking-[0.2em] text-[#8FA3B8]">MARITIME WORKFORCE OPERATING SYSTEM</span>
        </motion.div>

        <h1 className="font-display font-black uppercase tracking-tight leading-[0.95] text-4xl sm:text-6xl lg:text-7xl xl:text-8xl max-w-5xl">
          {HEADLINE.map((l, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span variants={line} custom={i} initial="hidden" animate="show" className="block">
                {i === 2 ? <span className="text-[#FF5E1E]">{l}</span> : l}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.6 }}
          className="mt-7 max-w-xl text-base sm:text-lg text-[#B8C4D0] leading-relaxed"
        >
          Start with training. Get workforce ready. Get placed. Build a career. Enter through <span className="text-white font-semibold">Firewatch</span> — advance into a skilled trade.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.6 }}
          className="mt-9 flex flex-wrap items-center gap-3"
        >
          <Button asChild data-testid="hero-cta-get-shipyard-ready" size="lg" className="rounded-none bg-[#FF5E1E] hover:bg-[#ff7038] text-[#0B0F14] font-display font-bold uppercase tracking-wide h-13 px-7 py-6 group">
            <a href="#apply">Start Your Career <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} /></a>
          </Button>
          <Button asChild data-testid="hero-cta-explore-pathway" size="lg" variant="outline" className="rounded-none border-white/25 bg-white/5 hover:bg-white/10 text-white font-display font-bold uppercase tracking-wide h-13 px-7 py-6">
            <a href="#pathway"><Play className="mr-2" size={16} /> Start Training</a>
          </Button>
          <Button onClick={() => setVideoOpen(true)} data-testid="hero-cta-watch-video" size="lg" variant="ghost" className="rounded-none text-[#8FA3B8] hover:text-white font-display font-bold uppercase tracking-wide h-13 px-5 py-6">
            <Play className="mr-2" size={16} /> Watch Video
          </Button>
        </motion.div>
      </div>

      <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} src={VIDEOS.welding} title="Inside The Shipyard" />
    </section>
  );
}
